/**
 * Chain tab — exercises every last_touch on-chain instruction the launch
 * checklist cares about. KotH path is fully wired; Session path is stubbed
 * (the ix args need session_id/players/state_hash inputs that don't yet exist
 * in the UI — adding a small form per row is the next step).
 */
import { BN } from "@coral-xyz/anchor";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { PublicKey, SystemProgram } from "@solana/web3.js";
import { Buffer } from "buffer";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  buildWalletProgram,
  deriveRoundPda,
  deriveSeasonPda,
  deriveLastTouchPda,
  deriveLeaderboardEntryPda,
} from "../../games/last-touch/anchor";
import { KOTH_ROUND_LS_KEY } from "../../games/last-touch/useKothLobbyController";
import { useKothRound } from "../../games/last-touch/useKothRound";
import {
  buildSessionKeyProgram,
  delegateSessionKey,
  getActiveSessionKey,
  revokeSessionKey,
  subscribeSessionKeyChange,
} from "../../session/sessionKey";
import { useChainNow } from "../../util/useChainNow";
import {
  SESSION_LS_KEY,
  deriveHostPda,
  deriveSessionPda,
  deriveTreasuryPda,
  hexToSessionId,
  randomSessionId,
  sessionIdToHex,
  sha256Hex,
  useSession,
  type SessionState,
} from "../session";
import { TestRow } from "../TestRow";
import type { TestResult } from "../types";

interface MethodsBuilder {
  accounts(accounts: Record<string, unknown>): MethodsBuilder;
  rpc(): Promise<string>;
}
interface RoundParamsArg {
  baseFeeLamports: BN;
  stepFeeLamports: BN;
  baseDurationSecs: number;
  extensionSecsPerTouch: number;
  maxTotalExtensionSecs: number;
  cooldownSecs: number;
  protocolSkimBps: number;
  vigBps: number;
  topNPayout: number;
  keeperBountyLamports: BN;
  cooldownSecsPerWallet: number;
  maxTouchesPerWallet: number;
  endgameThresholdSecs: number;
  extensionSecsEndgame: number;
  dynamicFeeBps: number;
}
interface MilestoneConfigArg {
  initialMilestoneMcapLamports: BN;
  multiplierBps: number;
  topK: number;
  minResidencySlots: BN;
  vaultFractionBps: number;
  mcapKeeper: PublicKey;
}
interface KothMethods {
  kothInitSeason(
    seasonId: number[],
    index: number,
    endsAt: BN,
    params: RoundParamsArg,
    milestone: MilestoneConfigArg,
  ): MethodsBuilder;
  kothUpdateRoundParams(params: RoundParamsArg): MethodsBuilder;
  kothPauseSeason(): MethodsBuilder;
  kothUnpauseSeason(): MethodsBuilder;
  kothInitRound(roundId: BN, durationSecs: BN): MethodsBuilder;
  kothTouch(): MethodsBuilder;
  kothEndRound(): MethodsBuilder;
  kothClaimRoundPayout(): MethodsBuilder;
  kothAdminAdjust(extendSecs: BN, forceEnd: boolean): MethodsBuilder;
  kothInitPayoutVault(): MethodsBuilder;
  kothSweepVaultToRound(): MethodsBuilder;
  kothCloseSeason(): MethodsBuilder;
  kothLatchMilestone(postedMcapLamports: BN): MethodsBuilder;
  kothDelegateSessionKey(delegate: PublicKey, durationSecs: BN): MethodsBuilder;
  kothRevokeSessionKey(): MethodsBuilder;
  kothTouchDelegated(): MethodsBuilder;
}
interface SessionMethods {
  initTreasury(
    admin: PublicKey,
    challengeWindowSecs: BN,
    hostTimeoutSecs: BN,
  ): MethodsBuilder;
  registerHost(region: string, capacity: number): MethodsBuilder;
  createSession(
    sessionId: number[],
    escrowLamports: BN,
    players: PublicKey[],
  ): MethodsBuilder;
  submitResult(stateHash: number[], winner: PublicKey): MethodsBuilder;
  claimPayout(): MethodsBuilder;
  finalizeResult(): MethodsBuilder;
  disputeResult(): MethodsBuilder;
  cancelSession(): MethodsBuilder;
  refundEscrow(): MethodsBuilder;
  adminResolveDispute(
    action: number,
    overrideWinner: PublicKey | null,
  ): MethodsBuilder;
}

const RESOLVE_ACTION_REFUND = 0;
const RESOLVE_ACTION_FINALIZE_OVERRIDE = 1;

const DEFAULT_DURATION_SECS = 60;
const DEFAULT_BURST_COUNT = 5;
const LAMPORTS_PER_SOL = 1_000_000_000;
const KOTH_BASE_FEE_LAMPORTS = 1_000_000;
const KOTH_STEP_FEE_LAMPORTS = 100_000;

function nextFeeLamports(touchCount: number): number {
  return KOTH_BASE_FEE_LAMPORTS + KOTH_STEP_FEE_LAMPORTS * touchCount;
}

function lamportsToSol(lamports: number | string | BN | bigint): string {
  let n: bigint;
  if (typeof lamports === "bigint") n = lamports;
  else if (typeof lamports === "number") n = BigInt(Math.floor(lamports));
  else n = BigInt(lamports.toString());
  const whole = n / BigInt(LAMPORTS_PER_SOL);
  const frac = n % BigInt(LAMPORTS_PER_SOL);
  return `${whole}.${frac.toString().padStart(9, "0").slice(0, 6)}`;
}

type RowId =
  | "initSeason"
  | "updateRoundParams"
  | "pauseSeason"
  | "unpauseSeason"
  | "refreshSeason"
  | "initRound"
  | "touch"
  | "burstTouch"
  | "endHappy"
  | "endBeforeExpiry"
  | "endZeroTouchers"
  | "claimRoundPayout"
  | "initPayoutVault"
  | "sweepVaultToRound"
  | "closeSeason"
  | "latchMilestone"
  | "refreshVault"
  | "refresh"
  | "extendQuick"
  | "extendCustom"
  | "forceEnd"
  | "initTreasury"
  | "registerHost"
  | "createSession"
  | "submitResult"
  | "claimPayout"
  | "finalizeResult"
  | "disputeResult"
  | "cancelSession"
  | "refundEscrow"
  | "adminResolveRefund"
  | "adminResolveFinalize"
  | "submitResultWrongHost"
  | "claimBeforeSubmit"
  | "claimDouble"
  | "delegateSessionKey"
  | "revokeSessionKey"
  | "touchDelegated";

const blank: TestResult = { status: "idle" };

export function ChainTab(): JSX.Element {
  const { connection } = useConnection();
  const wallet = useWallet();

  // Mirror the lobby controller: bump on delegate/revoke/hydrate so the
  // `getActiveSessionKey` read in touch handlers stays current.
  const [sessionKeyVersion, setSessionKeyVersion] = useState(0);
  useEffect(() => {
    return subscribeSessionKeyChange(() => {
      setSessionKeyVersion((v) => v + 1);
    });
  }, []);
  const activeSessionKey = useMemo(() => {
    if (!wallet.publicKey) return null;
    return getActiveSessionKey(wallet.publicKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.publicKey, sessionKeyVersion]);

  // Round id is sourced from `?round=<id>` so the panel and the in-world lobby
  // plaques share one source of truth. Writes go through setSearchParams (so
  // the lobby's `useKothLobbyController` picks them up) and the same
  // localStorage key the lobby caches into.
  const [searchParams, setSearchParams] = useSearchParams();
  const roundIdParam = searchParams.get("round");
  const roundId = useMemo<BN | null>(() => {
    const raw =
      roundIdParam ??
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(KOTH_ROUND_LS_KEY)
        : null);
    if (!raw) return null;
    try {
      return new BN(raw, 10);
    } catch {
      return null;
    }
  }, [roundIdParam]);
  const [roundIdInput, setRoundIdInput] = useState<string>(
    roundIdParam ?? "",
  );

  const setTrackedRoundId = useCallback(
    (id: BN) => {
      const s = id.toString();
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(KOTH_ROUND_LS_KEY, s);
      }
      setSearchParams({ round: s }, { replace: true });
      setRoundIdInput(s);
    },
    [setSearchParams],
  );

  const clearTrackedRoundId = useCallback(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(KOTH_ROUND_LS_KEY);
    }
    setSearchParams({}, { replace: true });
    setRoundIdInput("");
  }, [setSearchParams]);

  const { round, pda, refresh } = useKothRound(roundId);

  // Season tracking — the active season is the parent context for every new
  // round. The 32-byte season_id is stored as hex in localStorage so a session
  // started in one browser context is recoverable.
  const SEASON_LS_KEY = "solblox.season_id_hex";
  const [seasonIdHex, setSeasonIdHex] = useState<string>(() => {
    if (typeof localStorage === "undefined") return "";
    return localStorage.getItem(SEASON_LS_KEY) ?? "";
  });
  const seasonIdBytes = useMemo<Uint8Array | null>(() => {
    if (!seasonIdHex || seasonIdHex.length !== 64) return null;
    try {
      const out = new Uint8Array(32);
      for (let i = 0; i < 32; i++) {
        out[i] = parseInt(seasonIdHex.slice(i * 2, i * 2 + 2), 16);
      }
      return out;
    } catch {
      return null;
    }
  }, [seasonIdHex]);
  const seasonPda = useMemo<PublicKey | null>(() => {
    if (!seasonIdBytes) return null;
    try {
      return deriveSeasonPda(seasonIdBytes);
    } catch {
      return null;
    }
  }, [seasonIdBytes]);
  const [seasonAccount, setSeasonAccount] = useState<Record<string, unknown> | null>(null);
  const fetchSeason = useCallback(async () => {
    if (!seasonPda) {
      setSeasonAccount(null);
      return;
    }
    try {
      const program = buildWalletProgram(connection, wallet);
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["season"];
      if (!ns) {
        setSeasonAccount(null);
        return;
      }
      const acct = (await ns.fetch(seasonPda)) as Record<string, unknown>;
      setSeasonAccount(acct);
    } catch {
      setSeasonAccount(null);
    }
  }, [connection, wallet, seasonPda]);
  const persistSeasonId = useCallback((hex: string) => {
    if (typeof localStorage !== "undefined") {
      if (hex) localStorage.setItem(SEASON_LS_KEY, hex);
      else localStorage.removeItem(SEASON_LS_KEY);
    }
    setSeasonIdHex(hex);
  }, []);

  // Session id tracked via `?session=<hex>` URL param + localStorage cache.
  // Same source-of-truth pattern as the round id above; lets a session
  // created in one tab be reopened from a pasted URL or after a reload.
  const sessionIdHexParam = searchParams.get("session");
  const sessionId = useMemo<Uint8Array | null>(() => {
    const raw =
      sessionIdHexParam ??
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(SESSION_LS_KEY)
        : null);
    if (!raw) return null;
    try {
      return hexToSessionId(raw);
    } catch {
      return null;
    }
  }, [sessionIdHexParam]);
  const [sessionIdInput, setSessionIdInput] = useState<string>(
    sessionIdHexParam ?? "",
  );
  const setTrackedSessionId = useCallback(
    (id: Uint8Array) => {
      const hex = sessionIdToHex(id);
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(SESSION_LS_KEY, hex);
      }
      const next = new URLSearchParams(searchParams);
      next.set("session", hex);
      setSearchParams(next, { replace: true });
      setSessionIdInput(hex);
    },
    [searchParams, setSearchParams],
  );
  const clearTrackedSessionId = useCallback(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(SESSION_LS_KEY);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("session");
    setSearchParams(next, { replace: true });
    setSessionIdInput("");
  }, [searchParams, setSearchParams]);
  const {
    session,
    pda: sessionPda,
    refresh: refreshSession,
  } = useSession(sessionId);

  // Configurable inputs for tx parameters and burst behavior.
  const [durationInput, setDurationInput] = useState<string>(
    String(DEFAULT_DURATION_SECS),
  );
  const [burstCountInput, setBurstCountInput] = useState<string>(
    String(DEFAULT_BURST_COUNT),
  );

  const [extendCustomInput, setExtendCustomInput] = useState<string>("30");

  // Session-path form inputs.
  const [hostRegion, setHostRegion] = useState<string>("local");
  const [hostCapacity, setHostCapacity] = useState<string>("16");
  const [escrowSolInput, setEscrowSolInput] = useState<string>("0.05");
  const [playersInput, setPlayersInput] = useState<string>("");
  const [stateHashInput, setStateHashInput] = useState<string>(
    "0".repeat(64),
  );
  const [winnerInput, setWinnerInput] = useState<string>("");
  const [stateJsonInput, setStateJsonInput] = useState<string>(
    '{"score":1,"winner":"alice"}',
  );

  const [results, setResults] = useState<Record<RowId, TestResult>>({
    initSeason: blank,
    updateRoundParams: blank,
    pauseSeason: blank,
    unpauseSeason: blank,
    refreshSeason: blank,
    initRound: blank,
    touch: blank,
    burstTouch: blank,
    endHappy: blank,
    endBeforeExpiry: blank,
    endZeroTouchers: blank,
    claimRoundPayout: blank,
    initPayoutVault: blank,
    sweepVaultToRound: blank,
    closeSeason: blank,
    latchMilestone: blank,
    refreshVault: blank,
    refresh: blank,
    extendQuick: blank,
    extendCustom: blank,
    forceEnd: blank,
    initTreasury: blank,
    registerHost: blank,
    createSession: blank,
    submitResult: blank,
    claimPayout: blank,
    finalizeResult: blank,
    disputeResult: blank,
    cancelSession: blank,
    refundEscrow: blank,
    adminResolveRefund: blank,
    adminResolveFinalize: blank,
    submitResultWrongHost: blank,
    claimBeforeSubmit: blank,
    claimDouble: blank,
    delegateSessionKey: blank,
    revokeSessionKey: blank,
    touchDelegated: blank,
  });

  const setResult = useCallback((id: RowId, r: TestResult) => {
    setResults((prev) => ({ ...prev, [id]: r }));
  }, []);

  const runWith = useCallback(
    async (id: RowId, fn: () => Promise<{ sig?: string; output?: string }>) => {
      setResult(id, { status: "running", startedAt: Date.now() });
      try {
        const { sig, output } = await fn();
        setResult(id, {
          status: "ok",
          output,
          txSig: sig,
          finishedAt: Date.now(),
        });
      } catch (e) {
        const msg = (e as Error).message ?? String(e);
        setResult(id, {
          status: "fail",
          output: msg,
          finishedAt: Date.now(),
        });
      }
    },
    [setResult],
  );

  const walletReady = wallet.connected && !!wallet.publicKey;
  const disabled = !walletReady;

  const defaultRoundParams = useCallback((): RoundParamsArg => {
    return {
      baseFeeLamports: new BN(1_000_000),
      stepFeeLamports: new BN(100_000),
      baseDurationSecs: 600,
      extensionSecsPerTouch: 3,
      maxTotalExtensionSecs: 30,
      cooldownSecs: 0,
      protocolSkimBps: 500,
      vigBps: 500,
      topNPayout: 3,
      keeperBountyLamports: new BN(500_000),
      cooldownSecsPerWallet: 0,
      maxTouchesPerWallet: 1000,
      endgameThresholdSecs: 30,
      extensionSecsEndgame: 10,
      dynamicFeeBps: 200,
    };
  }, []);

  const defaultMilestoneConfig = useCallback((): MilestoneConfigArg => {
    if (!wallet.publicKey) {
      throw new Error("connect wallet");
    }
    return {
      // Start the milestone ladder at a low value so manual tests can latch
      // without astronomical posted-mcap numbers. Each subsequent latch
      // multiplies the threshold by `multiplierBps / 10_000` (2.0×).
      initialMilestoneMcapLamports: new BN(10).mul(new BN(LAMPORTS_PER_SOL)),
      multiplierBps: 20_000,
      topK: 3,
      // Must be >= 1 — MilestoneConfig::validate rejects 0 (audit milestone-3).
      // Surfpool ticks fast so 1 still latches on the happy path.
      minResidencySlots: new BN(1),
      // 10% of the spendable PayoutVault balance per crossed milestone.
      vaultFractionBps: 1_000,
      mcapKeeper: wallet.publicKey,
    };
  }, [wallet.publicKey]);

  const initSeason = useCallback(
    () =>
      runWith("initSeason", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        const seasonIdRaw = new Uint8Array(32);
        crypto.getRandomValues(seasonIdRaw);
        const hex = Array.from(seasonIdRaw)
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const newSeasonPda = deriveSeasonPda(seasonIdRaw, program.programId);
        const endsAt = new BN(Math.floor(Date.now() / 1000) + 24 * 60 * 60);
        const params = defaultRoundParams();
        const milestone = defaultMilestoneConfig();
        const sig = await methods
          .kothInitSeason(
            Array.from(seasonIdRaw),
            1,
            endsAt,
            params,
            milestone,
          )
          .accounts({
            season: newSeasonPda,
            admin: wallet.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        persistSeasonId(hex);
        void fetchSeason();
        return {
          sig,
          output: `season_id (hex) = ${hex.slice(0, 16)}…, pda = ${newSeasonPda.toBase58().slice(0, 8)}…`,
        };
      }),
    [
      connection,
      wallet,
      walletReady,
      defaultRoundParams,
      defaultMilestoneConfig,
      persistSeasonId,
      fetchSeason,
      runWith,
    ],
  );

  const updateRoundParams = useCallback(
    () =>
      runWith("updateRoundParams", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda) throw new Error("no tracked season — init_season first");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const params = defaultRoundParams();
        // Bump cooldown so the human can verify the update landed.
        params.cooldownSecsPerWallet = 5;
        const sig = await methods
          .kothUpdateRoundParams(params)
          .accounts({
            season: seasonPda,
            admin: wallet.publicKey,
          })
          .rpc();
        void fetchSeason();
        return {
          sig,
          output: "cooldown_secs_per_wallet bumped to 5; next init_round will snapshot it",
        };
      }),
    [connection, wallet, walletReady, seasonPda, defaultRoundParams, fetchSeason, runWith],
  );

  const pauseSeason = useCallback(
    () =>
      runWith("pauseSeason", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda) throw new Error("no tracked season");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const sig = await methods
          .kothPauseSeason()
          .accounts({ season: seasonPda, admin: wallet.publicKey })
          .rpc();
        void fetchSeason();
        return { sig, output: "season paused — next init_round will throw SeasonPaused" };
      }),
    [connection, wallet, walletReady, seasonPda, fetchSeason, runWith],
  );

  const unpauseSeason = useCallback(
    () =>
      runWith("unpauseSeason", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda) throw new Error("no tracked season");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const sig = await methods
          .kothUnpauseSeason()
          .accounts({ season: seasonPda, admin: wallet.publicKey })
          .rpc();
        void fetchSeason();
        return { sig, output: "season unpaused" };
      }),
    [connection, wallet, walletReady, seasonPda, fetchSeason, runWith],
  );

  const refreshSeason = useCallback(
    () =>
      runWith("refreshSeason", async () => {
        await fetchSeason();
        return {
          output: seasonPda ? `re-fetched ${seasonPda.toBase58()}` : "no season tracked",
        };
      }),
    [fetchSeason, seasonPda, runWith],
  );

  const initRound = useCallback(
    () =>
      runWith("initRound", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda) {
          throw new Error(
            "no tracked season — run koth_init_season first (a season is the parent context for every round since the Phase-1 cutover)",
          );
        }
        const durationSecs = Number(durationInput);
        if (!Number.isFinite(durationSecs) || durationSecs <= 0) {
          throw new Error(
            "invalid duration — enter a positive integer (seconds)",
          );
        }
        const id = new BN(Date.now());
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const newPda = deriveRoundPda(id, program.programId);
        const sig = await methods
          .kothInitRound(id, new BN(Math.floor(durationSecs)))
          .accounts({
            round: newPda,
            season: seasonPda,
            admin: wallet.publicKey,
            payer: wallet.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        setTrackedRoundId(id);
        return {
          sig,
          output: `round_id = ${id.toString()}, duration = ${Math.floor(durationSecs)}s, season = ${seasonPda.toBase58().slice(0, 8)}…`,
        };
      }),
    [
      connection,
      wallet,
      walletReady,
      durationInput,
      seasonPda,
      setTrackedRoundId,
      runWith,
    ],
  );

  const submitTouch = useCallback(
    () =>
      runWith("touch", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!roundId || !pda) throw new Error("no active round — init first");
        const owner = wallet.publicKey;
        const active = activeSessionKey;
        const lastTouch = deriveLastTouchPda(pda, owner);
        if (active) {
          const program = buildSessionKeyProgram(connection, active.keypair);
          const methods = program.methods as unknown as KothMethods;
          const sig = await methods
            .kothTouchDelegated()
            .accounts({
              round: pda,
              lastTouch,
              toucher: owner,
              sessionKeySigner: active.keypair.publicKey,
              sessionKey: active.sessionKeyPda,
              owner,
              systemProgram: SystemProgram.programId,
            })
            .rpc();
          refresh();
          return { sig, output: `touch (delegated) on ${pda.toBase58()}` };
        }
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const sig = await methods
          .kothTouch()
          .accounts({
            round: pda,
            lastTouch,
            toucher: owner,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        refresh();
        return { sig, output: `touch on ${pda.toBase58()}` };
      }),
    [connection, wallet, walletReady, roundId, pda, refresh, runWith, activeSessionKey],
  );

  const burstTouch = useCallback(
    () =>
      runWith("burstTouch", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!roundId || !pda) throw new Error("no active round — init first");
        const n = Number(burstCountInput);
        if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
          throw new Error("invalid burst count — enter a positive integer");
        }
        const owner = wallet.publicKey;
        const active = activeSessionKey;
        const lastTouch = deriveLastTouchPda(pda, owner);
        let lastSig = "";
        const sigs: string[] = [];
        if (active) {
          const program = buildSessionKeyProgram(connection, active.keypair);
          const methods = program.methods as unknown as KothMethods;
          for (let i = 0; i < n; i++) {
            const sig = await methods
              .kothTouchDelegated()
              .accounts({
                round: pda,
                lastTouch,
                toucher: owner,
                sessionKeySigner: active.keypair.publicKey,
                sessionKey: active.sessionKeyPda,
                owner,
                systemProgram: SystemProgram.programId,
              })
              .rpc();
            sigs.push(sig);
            lastSig = sig;
            refresh();
          }
        } else {
          const program = buildWalletProgram(connection, wallet);
          const methods = program.methods as unknown as KothMethods;
          for (let i = 0; i < n; i++) {
            const sig = await methods
              .kothTouch()
              .accounts({
                round: pda,
                lastTouch,
                toucher: owner,
                systemProgram: SystemProgram.programId,
              })
              .rpc();
            sigs.push(sig);
            lastSig = sig;
            refresh();
          }
        }
        return {
          sig: lastSig,
          output: `fired ${n} touches sequentially${active ? " (delegated)" : ""}\nfirst: ${sigs[0]}\nlast: ${lastSig}`,
        };
      }),
    [
      connection,
      wallet,
      walletReady,
      roundId,
      pda,
      refresh,
      burstCountInput,
      runWith,
      activeSessionKey,
    ],
  );

  const endHappy = useCallback(
    () =>
      runWith("endHappy", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!roundId || !pda || !round)
          throw new Error("no active round — init+touch first");
        if (!seasonPda)
          throw new Error("no season cached — init+select a season first");
        if (!round.season)
          throw new Error(
            "this round was inited before season + ring buffer existed — init fresh round and retry",
          );
        if (!round.recentTouchers)
          throw new Error(
            "this round was inited before recentTouchers existed — init fresh round and retry",
          );
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        // Phase 3: pass one LeaderboardEntry PDA per populated ring position.
        const populated = round.recentTouchers.filter(
          (p: PublicKey) => !p.equals(PublicKey.default),
        );
        if (populated.length === 0)
          throw new Error("ring buffer is empty — use 'end zero touchers' instead");
        const remaining = populated.map((wallet: PublicKey) => ({
          pubkey: deriveLeaderboardEntryPda(seasonPda, wallet),
          isSigner: false,
          isWritable: true,
        }));
        const sig = await (
          methods
            .kothEndRound()
            .accounts({
              round: pda,
              season: seasonPda,
              keeper: wallet.publicKey,
              systemProgram: SystemProgram.programId,
            }) as unknown as {
            remainingAccounts: (a: typeof remaining) => MethodsBuilder;
          }
        )
          .remainingAccounts(remaining)
          .rpc();
        refresh();
        return {
          sig,
          output: `settled to ${round.currentKing.toBase58().slice(0, 8)}…; credited ${populated.length} entries`,
        };
      }),
    [
      connection,
      wallet,
      walletReady,
      roundId,
      pda,
      round,
      seasonPda,
      refresh,
      runWith,
    ],
  );

  const endBeforeExpiry = useCallback(
    () =>
      runWith("endBeforeExpiry", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!roundId || !pda || !round)
          throw new Error("no active round — init first");
        const expiresAt = Number(round.expiresAt.toString());
        if (Math.floor(Date.now() / 1000) >= expiresAt) {
          throw new Error(
            "round has already expired — failure-path test requires a still-open round",
          );
        }
        if (!seasonPda)
          throw new Error("no season cached — init+select a season first");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const populated = (round.recentTouchers ?? []).filter(
          (p: PublicKey) => !p.equals(PublicKey.default),
        );
        const remaining = populated.map((wallet: PublicKey) => ({
          pubkey: deriveLeaderboardEntryPda(seasonPda, wallet),
          isSigner: false,
          isWritable: true,
        }));
        try {
          await (
            methods
              .kothEndRound()
              .accounts({
                round: pda,
                season: seasonPda,
                keeper: wallet.publicKey,
                systemProgram: SystemProgram.programId,
              }) as unknown as {
              remainingAccounts: (a: typeof remaining) => MethodsBuilder;
            }
          )
            .remainingAccounts(remaining)
            .rpc();
          throw new Error("expected end_round to fail before expiry, but it succeeded");
        } catch (e) {
          const msg = (e as Error).message ?? String(e);
          if (/expired|not.*expired|RoundNotExpired|0x/i.test(msg)) {
            return { output: `expected failure: ${msg}` };
          }
          throw e;
        }
      }),
    [connection, wallet, walletReady, roundId, pda, round, runWith],
  );

  const endZeroTouchers = useCallback(
    () =>
      runWith("endZeroTouchers", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!roundId || !pda || !round)
          throw new Error("no active round — init (and DO NOT touch) first");
        if (round.touchCount > 0) {
          throw new Error(
            `round has ${round.touchCount} touches — this test needs a zero-touch round`,
          );
        }
        const expiresAt = Number(round.expiresAt.toString());
        const now = Math.floor(Date.now() / 1000);
        if (now < expiresAt) {
          throw new Error(
            `round not yet expired (${expiresAt - now}s left) — wait for expiry then re-run`,
          );
        }
        if (!seasonPda)
          throw new Error("no season cached — init+select a season first");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        // Empty-round path: end_round still needs season; remainingAccounts is
        // empty since touch_count == 0 short-circuits before iterating.
        const sig = await (
          methods
            .kothEndRound()
            .accounts({
              round: pda,
              season: seasonPda,
              keeper: wallet.publicKey,
              systemProgram: SystemProgram.programId,
            }) as unknown as {
            remainingAccounts: (a: never[]) => MethodsBuilder;
          }
        )
          .remainingAccounts([])
          .rpc();
        refresh();
        return { sig, output: "settled with zero touchers" };
      }),
    [
      connection,
      wallet,
      walletReady,
      roundId,
      pda,
      round,
      seasonPda,
      refresh,
      runWith,
    ],
  );

  const claimRoundPayout = useCallback(
    () =>
      runWith("claimRoundPayout", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda)
          throw new Error("no season cached — init+select a season first");
        const entryPda = deriveLeaderboardEntryPda(seasonPda, wallet.publicKey);
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const sig = await methods
          .kothClaimRoundPayout()
          .accounts({
            entry: entryPda,
            wallet: wallet.publicKey,
          })
          .rpc();
        return {
          sig,
          output: `claimed leaderboard entry ${entryPda.toBase58().slice(0, 8)}…`,
        };
      }),
    [connection, wallet, walletReady, seasonPda, runWith],
  );

  // PayoutVault PDA — per-season, derived from the currently selected season.
  // Returns null when no season is tracked yet; vault-write buttons gate on it.
  const vaultPda = useMemo<PublicKey | null>(() => {
    if (!seasonPda) return null;
    return PublicKey.findProgramAddressSync(
      [Buffer.from("season_payout_vault"), seasonPda.toBuffer()],
      new PublicKey("DcRZHfX9T6fjbD768mG2e21aon224Fu8e8gsihR3R6uR"),
    )[0];
  }, [seasonPda]);
  const [vaultAccount, setVaultAccount] = useState<Record<string, unknown> | null>(
    null,
  );
  const fetchVault = useCallback(async () => {
    if (!vaultPda) {
      setVaultAccount(null);
      return;
    }
    try {
      const program = buildWalletProgram(connection, wallet);
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["payoutVault"];
      if (!ns) {
        setVaultAccount(null);
        return;
      }
      const acct = (await ns.fetch(vaultPda)) as Record<string, unknown>;
      setVaultAccount(acct);
    } catch {
      setVaultAccount(null);
    }
  }, [connection, wallet, vaultPda]);
  const refreshVault = useCallback(
    () =>
      runWith("refreshVault", async () => {
        await fetchVault();
        return { output: vaultAccount ? "vault fetched" : "vault not initialized" };
      }),
    [fetchVault, vaultAccount, runWith],
  );

  const initPayoutVault = useCallback(
    () =>
      runWith("initPayoutVault", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda || !vaultPda)
          throw new Error("no season cached — init+select a season first");
        const existing = await connection.getAccountInfo(vaultPda);
        if (existing !== null) {
          await fetchVault();
          return {
            output: `vault already initialized at ${vaultPda.toBase58().slice(0, 8)}… (per-season skip)`,
          };
        }
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const sig = await methods
          .kothInitPayoutVault()
          .accounts({
            season: seasonPda,
            vault: vaultPda,
            admin: wallet.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        await fetchVault();
        return { sig, output: `vault ${vaultPda.toBase58().slice(0, 8)}… ready` };
      }),
    [connection, wallet, walletReady, seasonPda, vaultPda, fetchVault, runWith],
  );

  const sweepVaultToRound = useCallback(
    () =>
      runWith("sweepVaultToRound", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda || !vaultPda)
          throw new Error("no season cached — init+select a season first");
        if (!pda || !round)
          throw new Error("no active round — init+load a round first");
        if (round.status !== 0)
          throw new Error("round must be Open to receive a sweep");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const sig = await methods
          .kothSweepVaultToRound()
          .accounts({
            season: seasonPda,
            vault: vaultPda,
            round: pda,
            admin: wallet.publicKey,
          })
          .rpc();
        await fetchVault();
        refresh();
        return { sig, output: "swept vault → round pot" };
      }),
    [connection, wallet, walletReady, seasonPda, vaultPda, pda, round, fetchVault, refresh, runWith],
  );

  const closeSeason = useCallback(
    () =>
      runWith("closeSeason", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda)
          throw new Error("no season cached — init+select a season first");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const sig = await methods
          .kothCloseSeason()
          .accounts({
            season: seasonPda,
            admin: wallet.publicKey,
          })
          .rpc();
        await fetchSeason();
        return { sig, output: "season closed" };
      }),
    [connection, wallet, walletReady, seasonPda, fetchSeason, runWith],
  );

  const [mcapInputSol, setMcapInputSol] = useState<string>("20");

  const latchMilestone = useCallback(
    () =>
      runWith("latchMilestone", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!seasonPda)
          throw new Error("no season cached — init+select a season first");
        if (!roundId) throw new Error("no tracked round — latch needs an active round PDA");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const mcapSol = Number(mcapInputSol);
        if (!Number.isFinite(mcapSol) || mcapSol <= 0)
          throw new Error("posted mcap must be a positive SOL number");
        const postedMcapLamports = new BN(Math.floor(mcapSol)).mul(new BN(LAMPORTS_PER_SOL));

        // Reload season to grab milestone_top_k and the current leaderboard
        // ranking. The latch ix expects exactly top_k LeaderboardEntry PDAs
        // in remaining_accounts, ordered by current rank.
        const ns = program.account as unknown as Record<
          string,
          {
            fetch: (pk: PublicKey) => Promise<unknown>;
            all: () => Promise<{ publicKey: PublicKey; account: Record<string, unknown> }[]>;
          }
        >;
        const seasonNs = ns["season"];
        const leaderboardNs = ns["leaderboardEntry"];
        if (!seasonNs || !leaderboardNs)
          throw new Error("program.account.{season,leaderboardEntry} unavailable");
        const seasonAcct = (await seasonNs.fetch(seasonPda)) as Record<string, unknown>;
        const topK = (seasonAcct.milestoneTopK ?? seasonAcct.milestone_top_k ?? 0) as number;
        if (!topK) throw new Error("season has milestoneTopK = 0");

        // Pull all LeaderboardEntries for this round PDA, sort by hold_score desc.
        const roundPda = deriveRoundPda(roundId);
        const entries = (await leaderboardNs.all()) as {
          publicKey: PublicKey;
          account: Record<string, unknown>;
        }[];
        const matched = entries.filter((e) => {
          const r = e.account.round as PublicKey;
          return r && r.toBase58 && r.toBase58() === roundPda.toBase58();
        });
        matched.sort((a, b) => {
          const sa = BigInt((a.account.holdScore ?? a.account.hold_score ?? 0).toString());
          const sb = BigInt((b.account.holdScore ?? b.account.hold_score ?? 0).toString());
          if (sb > sa) return 1;
          if (sb < sa) return -1;
          return 0;
        });
        if (matched.length < topK)
          throw new Error(`need ${topK} entries, only ${matched.length} found`);
        const winners = matched.slice(0, topK);

        const [vaultPda] = PublicKey.findProgramAddressSync(
          [Buffer.from("season_payout_vault"), seasonPda.toBuffer()],
          program.programId,
        );

        const builder = methods
          .kothLatchMilestone(postedMcapLamports)
          .accounts({
            season: seasonPda,
            round: roundPda,
            payoutVault: vaultPda,
            keeper: wallet.publicKey,
          });

        const remaining = winners.map((w) => ({
          pubkey: w.publicKey,
          isWritable: true,
          isSigner: false,
        }));
        (builder as unknown as { remainingAccounts: (a: unknown[]) => MethodsBuilder })
          .remainingAccounts(remaining);

        const sig = await builder.rpc();
        await fetchSeason();
        return {
          sig,
          output: `latched mcap = ${mcapSol} SOL, credited ${topK} winners`,
        };
      }),
    [
      connection,
      wallet,
      walletReady,
      seasonPda,
      roundId,
      mcapInputSol,
      fetchSeason,
      runWith,
    ],
  );

  const adjustRound = useCallback(
    async (
      rowId: "extendQuick" | "extendCustom" | "forceEnd",
      extendSecs: number,
      forceEnd: boolean,
      label: string,
    ): Promise<void> => {
      await runWith(rowId, async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!roundId || !pda || !round)
          throw new Error("no active round — init first");
        if (!round.keeper) {
          throw new Error(
            "this round was inited before the keeper field existed on KothRound (state-migration cutover). The old PDA bytes can't be admin-adjusted — init a fresh round and retry.",
          );
        }
        if (!round.keeper.equals(wallet.publicKey)) {
          throw new Error(
            `connected wallet is not the keeper of this round (keeper = ${round.keeper.toBase58().slice(0, 8)}…). Reconnect with the wallet that ran init_round.`,
          );
        }
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as KothMethods;
        const sig = await methods
          .kothAdminAdjust(new BN(extendSecs), forceEnd)
          .accounts({
            round: pda,
            keeper: wallet.publicKey,
          })
          .rpc();
        refresh();
        return { sig, output: label };
      });
    },
    [connection, wallet, walletReady, roundId, pda, round, refresh, runWith],
  );

  const extendQuick = useCallback(
    (secs: number) => () =>
      adjustRound(
        "extendQuick",
        secs,
        false,
        `${secs >= 0 ? "+" : ""}${secs}s applied to expires_at`,
      ),
    [adjustRound],
  );

  const extendCustom = useCallback(() => {
    const n = Number(extendCustomInput);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n === 0) {
      return runWith("extendCustom", async () => {
        throw new Error(
          "invalid extend amount — enter a non-zero integer (seconds)",
        );
      });
    }
    return adjustRound(
      "extendCustom",
      n,
      false,
      `${n > 0 ? "+" : ""}${n}s applied to expires_at`,
    );
  }, [adjustRound, extendCustomInput, runWith]);

  const forceEnd = useCallback(
    () => adjustRound("forceEnd", 0, true, "expires_at snapped to now - 1"),
    [adjustRound],
  );

  const refreshState = useCallback(
    () =>
      runWith("refresh", async () => {
        refresh();
        return { output: pda ? `re-fetching ${pda.toBase58()}` : "no PDA tracked" };
      }),
    [refresh, pda, runWith],
  );

  // --- KotH session keys -----------------------------------------------------
  // Reuse the production-flow helpers from `client/src/session/sessionKey.ts`
  // so the debug menu exercises the exact same delegate→touch_delegated→revoke
  // path the HUD button uses.

  const delegateSessionKeyAction = useCallback(
    () =>
      runWith("delegateSessionKey", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        const active = await delegateSessionKey(connection, wallet);
        const expiresInSecs = Math.max(
          0,
          active.expiresAt - Math.floor(Date.now() / 1000),
        );
        return {
          output: `delegate=${active.keypair.publicKey
            .toBase58()
            .slice(0, 8)}… pda=${active.sessionKeyPda
            .toBase58()
            .slice(0, 8)}… expires_in=${expiresInSecs}s`,
        };
      }),
    [connection, wallet, walletReady, runWith],
  );

  const revokeSessionKeyAction = useCallback(
    () =>
      runWith("revokeSessionKey", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        const sig = await revokeSessionKey(connection, wallet);
        return {
          sig: sig ?? undefined,
          output: sig
            ? "session key revoked on-chain + local cleared"
            : "no active session key — local cleared (no-op)",
        };
      }),
    [connection, wallet, walletReady, runWith],
  );

  const touchDelegatedAction = useCallback(
    () =>
      runWith("touchDelegated", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!roundId || !pda) throw new Error("no tracked round");
        const active = getActiveSessionKey(wallet.publicKey);
        if (!active) {
          throw new Error(
            "no active session key — run koth_delegate_session_key first",
          );
        }
        const program = buildSessionKeyProgram(connection, active.keypair);
        const methods = program.methods as unknown as KothMethods;
        const lastTouch = deriveLastTouchPda(pda, wallet.publicKey);
        const sig = await methods
          .kothTouchDelegated()
          .accounts({
            round: pda,
            lastTouch,
            toucher: wallet.publicKey,
            sessionKeySigner: active.keypair.publicKey,
            sessionKey: active.sessionKeyPda,
            owner: wallet.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        refresh();
        return {
          sig,
          output: `touch_delegated signed by ${active.keypair.publicKey
            .toBase58()
            .slice(0, 8)}… (no wallet popup)`,
        };
      }),
    [connection, wallet, walletReady, roundId, pda, refresh, runWith],
  );

  // Session-path callbacks. Each mirrors the host-side wiring in
  // `host/src/chain.ts` — the account shapes (signer, host PDA, treasury PDA)
  // are dictated by the constraints in `instructions/*.rs`, not by guesswork.

  const runInitTreasury = useCallback(
    () =>
      runWith("initTreasury", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        const program = buildWalletProgram(connection, wallet);
        const treasuryPda = deriveTreasuryPda(program.programId);
        const existing = await connection.getAccountInfo(treasuryPda);
        if (existing !== null) {
          return {
            output: `treasury already exists at ${treasuryPda.toBase58().slice(0, 8)}… (idempotent skip)`,
          };
        }
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .initTreasury(wallet.publicKey, new BN(5), new BN(10))
          .accounts({
            treasury: treasuryPda,
            payer: wallet.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        return {
          sig,
          output: `treasury initialised at ${treasuryPda.toBase58()} (admin = connected wallet)`,
        };
      }),
    [connection, wallet, walletReady, runWith],
  );

  const runRegisterHost = useCallback(
    () =>
      runWith("registerHost", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        const capN = Number(hostCapacity);
        if (!Number.isFinite(capN) || !Number.isInteger(capN) || capN <= 0) {
          throw new Error("invalid capacity — enter a positive integer");
        }
        if (hostRegion.length === 0 || hostRegion.length > 16) {
          throw new Error("region must be 1..=16 bytes (MAX_REGION_LEN)");
        }
        const program = buildWalletProgram(connection, wallet);
        const hostPda = deriveHostPda(wallet.publicKey, program.programId);
        const existing = await connection.getAccountInfo(hostPda);
        if (existing !== null) {
          return {
            output: `host already registered at ${hostPda.toBase58().slice(0, 8)}… (idempotent skip)`,
          };
        }
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .registerHost(hostRegion, capN)
          .accounts({
            host: hostPda,
            authority: wallet.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        return {
          sig,
          output: `host registered: region="${hostRegion}", capacity=${capN}`,
        };
      }),
    [connection, wallet, walletReady, hostRegion, hostCapacity, runWith],
  );

  const parsePlayers = useCallback((): PublicKey[] => {
    const lines = playersInput
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) {
      throw new Error(
        "players list is empty — enter one base58 pubkey per line",
      );
    }
    if (lines.length > 8) {
      throw new Error(
        `players list has ${lines.length} entries — MAX_PLAYERS=8 on-chain`,
      );
    }
    return lines.map((line, i) => {
      try {
        return new PublicKey(line);
      } catch {
        throw new Error(`line ${i + 1}: invalid base58 pubkey "${line}"`);
      }
    });
  }, [playersInput]);

  const runCreateSession = useCallback(
    () =>
      runWith("createSession", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        const escrowSol = Number(escrowSolInput);
        if (!Number.isFinite(escrowSol) || escrowSol <= 0) {
          throw new Error("invalid escrow — enter a positive number (SOL)");
        }
        const escrowLamports = Math.floor(escrowSol * LAMPORTS_PER_SOL);
        const players = parsePlayers();
        const idBytes = randomSessionId();
        const program = buildWalletProgram(connection, wallet);
        const hostPda = deriveHostPda(wallet.publicKey, program.programId);
        const hostInfo = await connection.getAccountInfo(hostPda);
        if (hostInfo === null) {
          throw new Error(
            "host PDA does not exist — run register_host first",
          );
        }
        const newSessionPda = deriveSessionPda(idBytes, program.programId);
        const treasuryPda = deriveTreasuryPda(program.programId);
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .createSession(
            Array.from(idBytes),
            new BN(escrowLamports),
            players,
          )
          .accounts({
            session: newSessionPda,
            host: hostPda,
            treasury: treasuryPda,
            payer: wallet.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        setTrackedSessionId(idBytes);
        return {
          sig,
          output: `session_id = ${sessionIdToHex(idBytes)}\npda = ${newSessionPda.toBase58()}\nescrow = ${escrowSol} SOL · players = ${players.length}`,
        };
      }),
    [
      connection,
      wallet,
      walletReady,
      escrowSolInput,
      parsePlayers,
      setTrackedSessionId,
      runWith,
    ],
  );

  const parseStateHashBytes = useCallback((hex: string): number[] => {
    const stripped = hex.startsWith("0x") ? hex.slice(2) : hex;
    if (stripped.length !== 64) {
      throw new Error(
        `state_hash must be 64 hex chars (got ${stripped.length})`,
      );
    }
    const out: number[] = [];
    for (let i = 0; i < 32; i++) {
      const byte = Number.parseInt(stripped.slice(i * 2, i * 2 + 2), 16);
      if (!Number.isFinite(byte)) {
        throw new Error(`invalid hex byte at offset ${i}`);
      }
      out.push(byte);
    }
    return out;
  }, []);

  const runSubmitResult = useCallback(
    () =>
      runWith("submitResult", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda)
          throw new Error("no active session — create one first");
        if (!winnerInput.trim())
          throw new Error("winner pubkey is empty");
        let winnerPk: PublicKey;
        try {
          winnerPk = new PublicKey(winnerInput.trim());
        } catch {
          throw new Error(`invalid winner pubkey: "${winnerInput.trim()}"`);
        }
        const hashBytes = parseStateHashBytes(stateHashInput);
        const program = buildWalletProgram(connection, wallet);
        const treasuryPda = deriveTreasuryPda(program.programId);
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .submitResult(hashBytes, winnerPk)
          .accounts({
            session: sessionPda,
            treasury: treasuryPda,
            hostSigner: wallet.publicKey,
          })
          .rpc();
        refreshSession();
        return {
          sig,
          output: `winner = ${winnerPk.toBase58().slice(0, 8)}…\nhash = ${stateHashInput.slice(0, 16)}…`,
        };
      }),
    [
      connection,
      wallet,
      walletReady,
      sessionId,
      sessionPda,
      stateHashInput,
      winnerInput,
      parseStateHashBytes,
      refreshSession,
      runWith,
    ],
  );

  const runHashStateJson = useCallback(async (): Promise<void> => {
    try {
      JSON.parse(stateJsonInput);
    } catch {
      setStateHashInput("INVALID_JSON");
      return;
    }
    const hex = await sha256Hex(stateJsonInput);
    setStateHashInput(hex);
  }, [stateJsonInput]);

  const runClaimPayout = useCallback(
    () =>
      runWith("claimPayout", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda)
          throw new Error("no active session — create one first");
        const program = buildWalletProgram(connection, wallet);
        const hostPda = deriveHostPda(wallet.publicKey, program.programId);
        const treasuryPda = deriveTreasuryPda(program.programId);
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .claimPayout()
          .accounts({
            session: sessionPda,
            host: wallet.publicKey,
            hostRecord: hostPda,
            treasury: treasuryPda,
          })
          .rpc();
        refreshSession();
        return { sig, output: `payout claimed against ${sessionPda.toBase58()}` };
      }),
    [
      connection,
      wallet,
      walletReady,
      sessionId,
      sessionPda,
      refreshSession,
      runWith,
    ],
  );

  // Challenge-window + refund path: each ix targets the *tracked* session PDA.

  const runFinalizeResult = useCallback(
    () =>
      runWith("finalizeResult", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda)
          throw new Error("no active session — create one first");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .finalizeResult()
          .accounts({
            session: sessionPda,
            caller: wallet.publicKey,
          })
          .rpc();
        refreshSession();
        return {
          sig,
          output: `session finalized → claim_payout is now callable`,
        };
      }),
    [connection, wallet, walletReady, sessionId, sessionPda, refreshSession, runWith],
  );

  const runDisputeResult = useCallback(
    () =>
      runWith("disputeResult", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda)
          throw new Error("no active session — create one first");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .disputeResult()
          .accounts({
            session: sessionPda,
            player: wallet.publicKey,
          })
          .rpc();
        refreshSession();
        return {
          sig,
          output: `dispute filed → session is frozen until admin resolves`,
        };
      }),
    [connection, wallet, walletReady, sessionId, sessionPda, refreshSession, runWith],
  );

  const runCancelSession = useCallback(
    () =>
      runWith("cancelSession", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda)
          throw new Error("no active session — create one first");
        const program = buildWalletProgram(connection, wallet);
        const treasuryPda = deriveTreasuryPda(program.programId);
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .cancelSession()
          .accounts({
            session: sessionPda,
            treasury: treasuryPda,
            caller: wallet.publicKey,
          })
          .rpc();
        refreshSession();
        return {
          sig,
          output: `session cancelled → players can now refund_escrow`,
        };
      }),
    [connection, wallet, walletReady, sessionId, sessionPda, refreshSession, runWith],
  );

  const runRefundEscrow = useCallback(
    () =>
      runWith("refundEscrow", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda)
          throw new Error("no active session — create one first");
        const program = buildWalletProgram(connection, wallet);
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .refundEscrow()
          .accounts({
            session: sessionPda,
            player: wallet.publicKey,
          })
          .rpc();
        refreshSession();
        return {
          sig,
          output: `escrow share drained to ${wallet.publicKey.toBase58().slice(0, 8)}…`,
        };
      }),
    [connection, wallet, walletReady, sessionId, sessionPda, refreshSession, runWith],
  );

  const runAdminResolve = useCallback(
    (action: number, rowId: "adminResolveRefund" | "adminResolveFinalize") =>
      runWith(rowId, async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda)
          throw new Error("no active session — create one first");
        const program = buildWalletProgram(connection, wallet);
        const treasuryPda = deriveTreasuryPda(program.programId);
        let overrideWinner: PublicKey | null = null;
        if (action === RESOLVE_ACTION_FINALIZE_OVERRIDE) {
          const trimmed = winnerInput.trim();
          if (trimmed.length > 0) {
            try {
              overrideWinner = new PublicKey(trimmed);
            } catch {
              throw new Error(`invalid override winner pubkey: "${trimmed}"`);
            }
          }
        }
        const methods = program.methods as unknown as SessionMethods;
        const sig = await methods
          .adminResolveDispute(action, overrideWinner)
          .accounts({
            session: sessionPda,
            treasury: treasuryPda,
            admin: wallet.publicKey,
          })
          .rpc();
        refreshSession();
        const label =
          action === RESOLVE_ACTION_REFUND ? "refund (→ Refundable)" : "finalize override (→ Finalized)";
        const ovrTxt = overrideWinner
          ? ` override_winner=${overrideWinner.toBase58().slice(0, 8)}…`
          : "";
        return { sig, output: `admin resolved as ${label}${ovrTxt}` };
      }),
    [
      connection,
      wallet,
      walletReady,
      sessionId,
      sessionPda,
      winnerInput,
      refreshSession,
      runWith,
    ],
  );

  const runAdminResolveRefund = useCallback(
    () => runAdminResolve(RESOLVE_ACTION_REFUND, "adminResolveRefund"),
    [runAdminResolve],
  );
  const runAdminResolveFinalize = useCallback(
    () =>
      runAdminResolve(
        RESOLVE_ACTION_FINALIZE_OVERRIDE,
        "adminResolveFinalize",
      ),
    [runAdminResolve],
  );

  // Failure paths mirror the KotH pattern: drive the program into the
  // rejected state, expect it to fail, and record the on-chain error message.

  const runSubmitResultWrongHost = useCallback(
    () =>
      runWith("submitResultWrongHost", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda || !session)
          throw new Error("no active session — create one first");
        if (session.host && session.host.equals(wallet.publicKey)) {
          throw new Error(
            "connected wallet IS the session host — this failure test needs a DIFFERENT signer than session.host. Switch wallets and re-run.",
          );
        }
        const hashBytes = parseStateHashBytes(stateHashInput);
        let winnerPk: PublicKey;
        try {
          winnerPk = new PublicKey(winnerInput.trim() || PublicKey.default.toBase58());
        } catch {
          winnerPk = PublicKey.default;
        }
        const program = buildWalletProgram(connection, wallet);
        const treasuryPda = deriveTreasuryPda(program.programId);
        const methods = program.methods as unknown as SessionMethods;
        try {
          await methods
            .submitResult(hashBytes, winnerPk)
            .accounts({
              session: sessionPda,
              treasury: treasuryPda,
              hostSigner: wallet.publicKey,
            })
            .rpc();
          throw new Error(
            "expected submit_result to fail when signer != session.host, but it succeeded",
          );
        } catch (e) {
          const msg = (e as Error).message ?? String(e);
          if (/host|UnauthorizedHost|constraint|0x/i.test(msg)) {
            return { output: `expected failure: ${msg}` };
          }
          throw e;
        }
      }),
    [
      connection,
      wallet,
      walletReady,
      sessionId,
      sessionPda,
      session,
      stateHashInput,
      winnerInput,
      parseStateHashBytes,
      runWith,
    ],
  );

  const runClaimBeforeSubmit = useCallback(
    () =>
      runWith("claimBeforeSubmit", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda || !session)
          throw new Error("no active session — create one first");
        if (session.status === 5 || session.status === 3) {
          throw new Error(
            `session.status = ${session.status} (Finalized/Paid) — this failure test needs a session that is NOT yet Finalized. Create a fresh session.`,
          );
        }
        const program = buildWalletProgram(connection, wallet);
        const hostPda = deriveHostPda(wallet.publicKey, program.programId);
        const treasuryPda = deriveTreasuryPda(program.programId);
        const methods = program.methods as unknown as SessionMethods;
        try {
          await methods
            .claimPayout()
            .accounts({
              session: sessionPda,
              host: wallet.publicKey,
              hostRecord: hostPda,
              treasury: treasuryPda,
            })
            .rpc();
          throw new Error(
            "expected claim_payout to fail before submit_result, but it succeeded",
          );
        } catch (e) {
          const msg = (e as Error).message ?? String(e);
          if (/status|state|InvalidSessionStatus|0x/i.test(msg)) {
            return { output: `expected failure: ${msg}` };
          }
          throw e;
        }
      }),
    [
      connection,
      wallet,
      walletReady,
      sessionId,
      sessionPda,
      session,
      runWith,
    ],
  );

  const runClaimDouble = useCallback(
    () =>
      runWith("claimDouble", async () => {
        if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
        if (!sessionId || !sessionPda || !session)
          throw new Error("no active session — create one first");
        if (session.status !== 3) {
          throw new Error(
            `session.status = ${session.status} — double-claim test requires status == 3 (Paid). Run claim_payout once first.`,
          );
        }
        const program = buildWalletProgram(connection, wallet);
        const hostPda = deriveHostPda(wallet.publicKey, program.programId);
        const treasuryPda = deriveTreasuryPda(program.programId);
        const methods = program.methods as unknown as SessionMethods;
        try {
          await methods
            .claimPayout()
            .accounts({
              session: sessionPda,
              host: wallet.publicKey,
              hostRecord: hostPda,
              treasury: treasuryPda,
            })
            .rpc();
          throw new Error(
            "expected second claim_payout to fail, but it succeeded",
          );
        } catch (e) {
          const msg = (e as Error).message ?? String(e);
          if (/status|state|already|paid|InvalidSessionStatus|0x/i.test(msg)) {
            return { output: `expected failure: ${msg}` };
          }
          throw e;
        }
      }),
    [
      connection,
      wallet,
      walletReady,
      sessionId,
      sessionPda,
      session,
      runWith,
    ],
  );


  const chainNow = useChainNow(1000);
  const expiresAt = round ? Number(round.expiresAt.toString()) : 0;
  const secsLeft = round ? expiresAt - chainNow : 0;
  const stateLine = round
    ? `pot=${round.potLamports.toString()} lamports · touches=${round.touchCount} · status=${round.status === 0 ? "Open" : "Settled"} · t-${secsLeft}s`
    : roundId
      ? "PDA fetching… (or round_id has no on-chain account yet)"
      : "no round tracked in panel — run init_round, paste a round_id below, or open a round via ?round=<id> in the URL";

  const loadFromInput = (): void => {
    if (!roundIdInput.trim()) return;
    try {
      const parsed = new BN(roundIdInput.trim(), 10);
      setTrackedRoundId(parsed);
    } catch {
      // ignore — input stays so user can fix it
    }
  };

  const clearTracked = (): void => {
    clearTrackedRoundId();
  };

  const loadSessionFromInput = (): void => {
    if (!sessionIdInput.trim()) return;
    try {
      const bytes = hexToSessionId(sessionIdInput.trim());
      setTrackedSessionId(bytes);
    } catch {
      // ignore — input stays so user can fix it
    }
  };

  return (
    <div>
      <ContextBar
        wallet={walletReady ? wallet.publicKey?.toBase58() ?? null : null}
        roundId={roundId?.toString() ?? null}
        stateLine={stateLine}
      />

      <RoundLoader
        input={roundIdInput}
        onInputChange={setRoundIdInput}
        onLoad={loadFromInput}
        onClear={clearTracked}
        hasTracked={roundId !== null}
      />

      <SectionHeader>KotH — season</SectionHeader>
      <div style={{ fontFamily: "monospace", fontSize: 12, opacity: 0.8, padding: "4px 8px" }}>
        season_id (hex):{" "}
        <input
          value={seasonIdHex}
          onChange={(e) => persistSeasonId(e.target.value.trim())}
          placeholder="(none — run koth_init_season to mint one)"
          style={{ width: 540, fontFamily: "monospace" }}
        />
        {seasonPda && (
          <div style={{ marginTop: 4 }}>
            pda: {seasonPda.toBase58()}
            {seasonAccount && (
              <>
                {" "}· paused={String((seasonAccount.paused as boolean) ?? "?")}
                {" "}· rounds_settled={String((seasonAccount.roundsSettled as number) ?? "?")}
                {" "}· admin={(seasonAccount.admin as PublicKey | undefined)?.toBase58().slice(0, 8)}…
              </>
            )}
          </div>
        )}
      </div>
      <TestRow
        label="koth_init_season"
        hint="Generates a random 32-byte season_id, inits a Season PDA with default RoundParams (ends_at = now + 24h). The hex id is persisted in localStorage."
        result={results.initSeason}
        onRun={initSeason}
        disabled={disabled}
      />
      <TestRow
        label="koth_update_round_params"
        hint="Bumps cooldown_secs_per_wallet on the tracked season's params to 5. Affects the NEXT init_round; rounds in flight retain their snapshot."
        result={results.updateRoundParams}
        onRun={updateRoundParams}
        disabled={disabled || !seasonPda}
      />
      <TestRow
        label="koth_pause_season"
        hint="Flips season.paused = true. Subsequent koth_init_round throws SeasonPaused."
        result={results.pauseSeason}
        onRun={pauseSeason}
        disabled={disabled || !seasonPda}
      />
      <TestRow
        label="koth_unpause_season"
        hint="Flips season.paused = false."
        result={results.unpauseSeason}
        onRun={unpauseSeason}
        disabled={disabled || !seasonPda}
      />
      <TestRow
        label="refresh season state"
        hint="Re-poll the Season PDA."
        result={results.refreshSeason}
        onRun={refreshSeason}
        disabled={!seasonPda}
      />

      <SectionHeader>KotH — config</SectionHeader>
      <RoundConfigBar
        durationInput={durationInput}
        onDurationChange={setDurationInput}
        burstCountInput={burstCountInput}
        onBurstCountChange={setBurstCountInput}
        currentPotLamports={round?.potLamports ?? null}
        currentTouchCount={round?.touchCount ?? null}
      />

      <SectionHeader>KotH — happy path</SectionHeader>
      <TestRow
        label="koth_init_round"
        hint={`Init a fresh round with round_id = Date.now(). Uses the duration set in the config bar above (currently ${durationInput || "?"}s).`}
        result={results.initRound}
        onRun={initRound}
        disabled={disabled}
      />
      <TestRow
        label="koth_touch"
        hint="Touch the tracked round. Pot increases, current_king becomes your wallet, timer extends."
        result={results.touch}
        onRun={submitTouch}
        disabled={disabled || !roundId}
      />
      <TestRow
        label={`koth_touch × N (burst — fires ${burstCountInput || "?"} sequential touches)`}
        hint="Fast way to grow the pot for testing payout splits. Runs sequentially so each touch sees the previous fee step."
        result={results.burstTouch}
        onRun={burstTouch}
        disabled={disabled || !roundId}
      />
      <TestRow
        label="koth_end_round (sole-king path)"
        hint="Settle after expiry. winner_two = default → end_round.rs rolls runner-up share into winner_one."
        result={results.endHappy}
        onRun={endHappy}
        disabled={disabled || !roundId}
      />
      <TestRow
        label="refresh round state"
        hint="Re-poll the KothRound PDA. Use after on-chain changes from another tab/wallet."
        result={results.refresh}
        onRun={refreshState}
      />

      <SectionHeader>KotH — failure paths</SectionHeader>
      <TestRow
        label="end_round before expiry (must fail)"
        hint="Confirms the program rejects early settlement. Passes when the rpc returns an error."
        result={results.endBeforeExpiry}
        onRun={endBeforeExpiry}
        disabled={disabled || !roundId}
      />
      <TestRow
        label="end_round with zero touchers"
        hint="Init a round, wait for expiry without touching, then end. Pot should be empty, no winner."
        result={results.endZeroTouchers}
        onRun={endZeroTouchers}
        disabled={disabled || !roundId}
      />

      <SectionHeader>KotH — payouts</SectionHeader>
      <TestRow
        label="claim_round_payout (connected wallet)"
        hint="Drain unclaimed lamports from the LeaderboardEntry PDA for the connected wallet under the cached season. Must follow at least one settled round where this wallet landed in the top-N ring."
        result={results.claimRoundPayout}
        onRun={claimRoundPayout}
        disabled={disabled || !seasonPda}
      />

      <SectionHeader>KotH — season lifecycle</SectionHeader>
      <TestRow
        label="init_payout_vault (per season)"
        hint="One-time bootstrap of the per-season PayoutVault PDA. Requires a cached season. Subsequent calls fail with 'already in use' — that's expected. The connected wallet must be the season admin."
        result={results.initPayoutVault}
        onRun={initPayoutVault}
        disabled={disabled}
      />
      <TestRow
        label="refresh PayoutVault state"
        hint="Read the vault PDA and surface lifetime in/out lamport totals."
        result={results.refreshVault}
        onRun={refreshVault}
        disabled={disabled}
      />
      {vaultAccount && (
        <div style={{ fontSize: 12, opacity: 0.7, marginLeft: 12 }}>
          vault.admin: {String((vaultAccount.admin as PublicKey)?.toBase58?.() ?? "—")}<br />
          lifetime_inflow: {String((vaultAccount.lifetimeInflowLamports as BN)?.toString?.() ?? "—")}{" "}lamports<br />
          lifetime_outflow: {String((vaultAccount.lifetimeOutflowLamports as BN)?.toString?.() ?? "—")}{" "}lamports
        </div>
      )}
      <TestRow
        label="sweep_vault_to_round"
        hint="Drains all lamports above the vault's rent reserve into the currently-loaded round's pot. Vault.admin signer required; round must be Open."
        result={results.sweepVaultToRound}
        onRun={sweepVaultToRound}
        disabled={disabled || !roundId || !round || round?.status !== 0}
      />
      <TestRow
        label="close_season"
        hint="Admin-only teardown. Requires now ≥ season.ends_at. Refunds the Season PDA's rent to the admin signer."
        result={results.closeSeason}
        onRun={closeSeason}
        disabled={disabled || !seasonPda}
      />

      <SectionHeader>KotH — mcap milestone latch</SectionHeader>
      <LatchMilestoneRow
        mcapInputSol={mcapInputSol}
        onMcapChange={setMcapInputSol}
        result={results.latchMilestone}
        onRun={latchMilestone}
        disabled={disabled || !seasonPda || !roundId}
      />

      <SectionHeader>KotH — mid-round controls</SectionHeader>
      <QuickExtendRow
        result={results.extendQuick}
        onExtend={(secs) => void extendQuick(secs)()}
        disabled={disabled || !roundId || !round || round?.status !== 0}
      />
      <CustomExtendRow
        input={extendCustomInput}
        onInputChange={setExtendCustomInput}
        result={results.extendCustom}
        onRun={() => void extendCustom()}
        disabled={disabled || !roundId || !round || round?.status !== 0}
      />
      <TestRow
        label="koth_admin_adjust (force_end)"
        hint="Snaps expires_at to now-1 so the next end_round call settles immediately. Keeper-only. Useful when you want to settle without waiting out the timer."
        result={results.forceEnd}
        onRun={() => void forceEnd()}
        disabled={disabled || !roundId || !round || round?.status !== 0}
      />

      <SectionHeader>KotH — session keys (popup-free touches)</SectionHeader>
      <TestRow
        label="koth_delegate_session_key"
        hint="Generates an ephemeral keypair, tops it up from your wallet, and authorizes it on-chain for 15 minutes. One wallet popup. Persists the secret to IndexedDB (localStorage fallback) so subsequent touch_delegated calls need no popup."
        result={results.delegateSessionKey}
        onRun={() => void delegateSessionKeyAction()}
        disabled={disabled}
      />
      <TestRow
        label="koth_touch_delegated"
        hint="Touches the tracked round signed by the cached session key. toucher=you, fee + last_touch rent paid by the delegate. No wallet popup. Requires an active session key — run koth_delegate_session_key first."
        result={results.touchDelegated}
        onRun={() => void touchDelegatedAction()}
        disabled={disabled || !roundId}
      />
      <TestRow
        label="koth_revoke_session_key"
        hint="Closes the SessionKey PDA on-chain (rent → owner) and clears the cached secret. Safe to call when no key is active."
        result={results.revokeSessionKey}
        onRun={() => void revokeSessionKeyAction()}
        disabled={disabled}
      />

      <SectionHeader>Session — config</SectionHeader>
      <SessionLoader
        input={sessionIdInput}
        onInputChange={setSessionIdInput}
        onLoad={loadSessionFromInput}
        onClear={clearTrackedSessionId}
        hasTracked={sessionId !== null}
      />
      <SessionContextBar
        sessionId={sessionId ? sessionIdToHex(sessionId) : null}
        sessionPda={sessionPda?.toBase58() ?? null}
        session={session}
      />
      <SessionFormBar
        hostRegion={hostRegion}
        onHostRegionChange={setHostRegion}
        hostCapacity={hostCapacity}
        onHostCapacityChange={setHostCapacity}
        escrowSolInput={escrowSolInput}
        onEscrowSolChange={setEscrowSolInput}
        playersInput={playersInput}
        onPlayersChange={setPlayersInput}
        stateHashInput={stateHashInput}
        onStateHashChange={setStateHashInput}
        winnerInput={winnerInput}
        onWinnerChange={setWinnerInput}
        stateJsonInput={stateJsonInput}
        onStateJsonChange={setStateJsonInput}
        onHashStateJson={() => void runHashStateJson()}
        onSelfWinner={() => {
          if (wallet.publicKey) setWinnerInput(wallet.publicKey.toBase58());
        }}
      />

      <SectionHeader>Session — prerequisites</SectionHeader>
      <TestRow
        label="init_treasury (one-time, idempotent)"
        hint="Initialise the singleton Treasury PDA. Permissionless. Must exist before any claim_payout can settle the 2% protocol fee."
        result={results.initTreasury}
        onRun={runInitTreasury}
        disabled={disabled}
      />
      <TestRow
        label="register_host (idempotent)"
        hint={`Register the connected wallet as a host. region="${hostRegion}", capacity=${hostCapacity}. Required before create_session.`}
        result={results.registerHost}
        onRun={runRegisterHost}
        disabled={disabled}
      />

      <SectionHeader>Session — happy path</SectionHeader>
      <TestRow
        label="create_session"
        hint={`Mint a fresh Session PDA with a random session_id. Escrows ${escrowSolInput || "?"} SOL from the connected wallet and stamps the players list above. Tracked via ?session=<hex>.`}
        result={results.createSession}
        onRun={runCreateSession}
        disabled={disabled}
      />
      <TestRow
        label="submit_result"
        hint="Host posts the canonical state_hash + winner. Drives status Created/Live → ResultSubmitted. Use the 'hash JSON' helper above to populate state_hash."
        result={results.submitResult}
        onRun={runSubmitResult}
        disabled={disabled || !sessionId}
      />
      <TestRow
        label="finalize_result"
        hint="Permissionless. Advances ResultSubmitted → Finalized once the challenge window has elapsed (CHALLENGE_WINDOW_SECS on-chain)."
        result={results.finalizeResult}
        onRun={runFinalizeResult}
        disabled={disabled || !sessionId}
      />
      <TestRow
        label="claim_payout"
        hint="Drains escrow_lamports, splits 2% to Treasury, rest to the host wallet. Requires status == Finalized. Drives status → Paid."
        result={results.claimPayout}
        onRun={runClaimPayout}
        disabled={disabled || !sessionId}
      />

      <SectionHeader>Session — dispute &amp; refund</SectionHeader>
      <TestRow
        label="dispute_result"
        hint="Player on the allowlist freezes a ResultSubmitted session into Disputed before its dispute_deadline. Connected wallet must be on session.players."
        result={results.disputeResult}
        onRun={runDisputeResult}
        disabled={disabled || !sessionId}
      />
      <TestRow
        label="admin_resolve_dispute → refund"
        hint="Treasury admin sends a Disputed session to Refundable. Players can then drain via refund_escrow. Connected wallet must equal treasury.admin."
        result={results.adminResolveRefund}
        onRun={runAdminResolveRefund}
        disabled={disabled || !sessionId}
      />
      <TestRow
        label="admin_resolve_dispute → finalize override"
        hint="Treasury admin sends a Disputed session to Finalized. If the winner pubkey field above is set and on the allowlist, overrides session.winner first."
        result={results.adminResolveFinalize}
        onRun={runAdminResolveFinalize}
        disabled={disabled || !sessionId}
      />
      <TestRow
        label="cancel_session"
        hint="Cancel a stuck Created/Live session. Host can cancel anytime; non-host signers must wait out HOST_TIMEOUT_SECS (or 2× for Live)."
        result={results.cancelSession}
        onRun={runCancelSession}
        disabled={disabled || !sessionId}
      />
      <TestRow
        label="refund_escrow"
        hint="Drain the connected wallet's pro-rata share of escrow. Requires status == Cancelled or Refundable AND wallet on session.players."
        result={results.refundEscrow}
        onRun={runRefundEscrow}
        disabled={disabled || !sessionId}
      />

      <SectionHeader>Session — failure paths</SectionHeader>
      <TestRow
        label="submit_result with wrong signer (must fail)"
        hint="Confirms the program rejects submit_result when host_signer != session.host. Switch wallets so the connected wallet is NOT the session host, then run."
        result={results.submitResultWrongHost}
        onRun={runSubmitResultWrongHost}
        disabled={disabled || !sessionId}
      />
      <TestRow
        label="claim_payout before finalize_result (must fail)"
        hint="Create a fresh session, submit_result if you like — but do NOT finalize_result, then run this. Program should reject because status != Finalized."
        result={results.claimBeforeSubmit}
        onRun={runClaimBeforeSubmit}
        disabled={disabled || !sessionId}
      />
      <TestRow
        label="claim_payout twice (must fail)"
        hint="Run claim_payout once successfully (status → Paid), then re-run. Program should reject the second claim."
        result={results.claimDouble}
        onRun={runClaimDouble}
        disabled={disabled || !sessionId}
      />
    </div>
  );
}

function RoundConfigBar({
  durationInput,
  onDurationChange,
  burstCountInput,
  onBurstCountChange,
  currentPotLamports,
  currentTouchCount,
}: {
  durationInput: string;
  onDurationChange: (v: string) => void;
  burstCountInput: string;
  onBurstCountChange: (v: string) => void;
  currentPotLamports: BN | null;
  currentTouchCount: number | null;
}): JSX.Element {
  const burstN = Number(burstCountInput);
  const startTouchCount = currentTouchCount ?? 0;
  let projectedAddedLamports = 0;
  if (Number.isFinite(burstN) && burstN > 0) {
    for (let i = 0; i < Math.min(burstN, 1000); i++) {
      projectedAddedLamports += nextFeeLamports(startTouchCount + i);
    }
  }
  const currentPot = currentPotLamports
    ? BigInt(currentPotLamports.toString())
    : 0n;
  const projectedPot = currentPot + BigInt(projectedAddedLamports);

  return (
    <div
      style={{
        padding: "8px 10px",
        background: "#0e0e15",
        borderBottom: "1px solid #1c1c26",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <ConfigRow
        label="round duration (s)"
        input={
          <input
            type="number"
            min={1}
            step={1}
            value={durationInput}
            onChange={(e) => onDurationChange(e.target.value)}
            style={configInputStyle}
          />
        }
        hint="Used by koth_init_round below. Short durations are good for testing the expire path quickly."
      />
      <ConfigRow
        label="burst touch count (N)"
        input={
          <input
            type="number"
            min={1}
            step={1}
            value={burstCountInput}
            onChange={(e) => onBurstCountChange(e.target.value)}
            style={configInputStyle}
          />
        }
        hint="How many sequential touches the burst button fires. Each touch costs the next fee step."
      />
      <div
        style={{
          color: "#cfd2dc",
          fontSize: 11,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          lineHeight: 1.6,
          paddingTop: 4,
          borderTop: "1px solid #1c1c26",
        }}
      >
        <div>
          current pot: {lamportsToSol(currentPot)} SOL ({currentPot.toString()}{" "}
          lamports)
        </div>
        <div>
          next touch fee:{" "}
          {currentTouchCount === null
            ? "—"
            : `${lamportsToSol(nextFeeLamports(startTouchCount))} SOL`}
        </div>
        <div>
          burst would add: {lamportsToSol(projectedAddedLamports)} SOL (→
          projected pot {lamportsToSol(projectedPot)} SOL)
        </div>
      </div>
    </div>
  );
}

const configInputStyle: React.CSSProperties = {
  width: 100,
  padding: "4px 8px",
  background: "#08080d",
  border: "1px solid #2a2a36",
  borderRadius: 4,
  color: "#e8e8ef",
  fontSize: 11,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

function ConfigRow({
  label,
  input,
  hint,
}: {
  label: string;
  input: React.ReactNode;
  hint: string;
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div
        style={{
          color: "#7a8098",
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: 1,
          minWidth: 130,
          paddingTop: 4,
        }}
      >
        {label}
      </div>
      <div>{input}</div>
      <div
        style={{
          color: "#7a8098",
          fontSize: 11,
          flex: 1,
          paddingTop: 4,
        }}
      >
        {hint}
      </div>
    </div>
  );
}

function RoundLoader({
  input,
  onInputChange,
  onLoad,
  onClear,
  hasTracked,
}: {
  input: string;
  onInputChange: (v: string) => void;
  onLoad: () => void;
  onClear: () => void;
  hasTracked: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        padding: "8px 10px",
        background: "#0e0e15",
        borderBottom: "1px solid #1c1c26",
        display: "flex",
        gap: 6,
        alignItems: "center",
      }}
    >
      <input
        type="text"
        placeholder="paste round_id (decimal u64) to track an existing round"
        value={input}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onLoad();
        }}
        style={{
          flex: 1,
          padding: "5px 8px",
          background: "#08080d",
          border: "1px solid #2a2a36",
          borderRadius: 4,
          color: "#e8e8ef",
          fontSize: 11,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      />
      <button
        onClick={onLoad}
        style={{
          padding: "5px 10px",
          background: "#3a3a4e",
          color: "#e8e8ef",
          border: "1px solid #2a2a36",
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Load
      </button>
      {hasTracked && (
        <button
          onClick={onClear}
          style={{
            padding: "5px 8px",
            background: "transparent",
            color: "#9aa0b4",
            border: "1px solid #2a2a36",
            borderRadius: 4,
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

function ContextBar({
  wallet,
  roundId,
  stateLine,
}: {
  wallet: string | null;
  roundId: string | null;
  stateLine: string;
}): JSX.Element {
  return (
    <div
      style={{
        padding: "8px 10px",
        background: "#0a0a12",
        borderBottom: "1px solid #1c1c26",
        color: "#cfd2dc",
        fontSize: 11,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        lineHeight: 1.6,
      }}
    >
      <div>
        wallet:{" "}
        {wallet ? `${wallet.slice(0, 8)}…${wallet.slice(-6)}` : "(disconnected)"}
      </div>
      <div>round_id: {roundId ?? "(none)"}</div>
      <div>state: {stateLine}</div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        padding: "6px 10px",
        background: "#15151f",
        color: "#9aa0b4",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        borderTop: "1px solid #1c1c26",
        borderBottom: "1px solid #1c1c26",
      }}
    >
      {children}
    </div>
  );
}

function QuickExtendRow({
  result,
  onExtend,
  disabled,
}: {
  result: TestResult;
  onExtend: (secs: number) => void;
  disabled: boolean;
}): JSX.Element {
  const running = result.status === "running";
  const btnDisabled = disabled || running;
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: "8px 10px",
        borderBottom: "1px solid #1c1c26",
        background: "#0e0e15",
      }}
    >
      <div style={{ display: "flex", gap: 4, flex: "0 0 auto" }}>
        <QuickBtn label="+30s" onClick={() => onExtend(30)} disabled={btnDisabled} />
        <QuickBtn label="+60s" onClick={() => onExtend(60)} disabled={btnDisabled} />
        <QuickBtn label="-30s" onClick={() => onExtend(-30)} disabled={btnDisabled} />
      </div>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={{ color: "#e8e8ef", fontSize: 12, fontWeight: 600 }}>
          koth_admin_adjust (quick extend)
        </div>
        <div style={{ color: "#7a8098", fontSize: 11, marginTop: 2 }}>
          Add/subtract seconds from expires_at on the live round. Keeper-only.
          Useful for stretching a round mid-play.
        </div>
        {result.output && (
          <div
            style={{
              color: "#cfd2dc",
              fontSize: 11,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              marginTop: 4,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {result.output}
          </div>
        )}
        {result.txSig && (
          <div
            style={{
              color: "#7ab0ff",
              fontSize: 11,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              marginTop: 4,
            }}
          >
            tx: {result.txSig.slice(0, 8)}…{result.txSig.slice(-6)}
          </div>
        )}
      </div>
      <div style={{ flex: "0 0 auto" }}>
        <StatusPillInline status={result.status} />
      </div>
    </div>
  );
}

function CustomExtendRow({
  input,
  onInputChange,
  result,
  onRun,
  disabled,
}: {
  input: string;
  onInputChange: (v: string) => void;
  result: TestResult;
  onRun: () => void;
  disabled: boolean;
}): JSX.Element {
  const running = result.status === "running";
  const btnDisabled = disabled || running;
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: "8px 10px",
        borderBottom: "1px solid #1c1c26",
        background: "#0e0e15",
      }}
    >
      <div style={{ display: "flex", gap: 4, flex: "0 0 auto", alignItems: "center" }}>
        <input
          type="number"
          step={1}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          style={{
            width: 70,
            padding: "4px 6px",
            background: "#08080d",
            border: "1px solid #2a2a36",
            borderRadius: 4,
            color: "#e8e8ef",
            fontSize: 11,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        />
        <button
          onClick={onRun}
          disabled={btnDisabled}
          style={{
            padding: "4px 10px",
            background: btnDisabled ? "#22222e" : "#3a3a4e",
            color: btnDisabled ? "#5a5a72" : "#e8e8ef",
            border: "1px solid #2a2a36",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            cursor: btnDisabled ? "not-allowed" : "pointer",
          }}
        >
          Run
        </button>
      </div>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={{ color: "#e8e8ef", fontSize: 12, fontWeight: 600 }}>
          koth_admin_adjust (custom extend)
        </div>
        <div style={{ color: "#7a8098", fontSize: 11, marginTop: 2 }}>
          Apply an arbitrary signed delta (seconds) to expires_at. Negative
          values shrink the round.
        </div>
        {result.output && (
          <div
            style={{
              color: "#cfd2dc",
              fontSize: 11,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              marginTop: 4,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {result.output}
          </div>
        )}
        {result.txSig && (
          <div
            style={{
              color: "#7ab0ff",
              fontSize: 11,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              marginTop: 4,
            }}
          >
            tx: {result.txSig.slice(0, 8)}…{result.txSig.slice(-6)}
          </div>
        )}
      </div>
      <div style={{ flex: "0 0 auto" }}>
        <StatusPillInline status={result.status} />
      </div>
    </div>
  );
}

function LatchMilestoneRow({
  mcapInputSol,
  onMcapChange,
  result,
  onRun,
  disabled,
}: {
  mcapInputSol: string;
  onMcapChange: (v: string) => void;
  result: TestResult;
  onRun: () => void;
  disabled: boolean;
}): JSX.Element {
  const running = result.status === "running";
  const btnDisabled = disabled || running;
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: "8px 10px",
        borderBottom: "1px solid #1c1c26",
        background: "#0e0e15",
      }}
    >
      <div style={{ display: "flex", gap: 4, flex: "0 0 auto", alignItems: "center" }}>
        <input
          type="number"
          step={1}
          value={mcapInputSol}
          onChange={(e) => onMcapChange(e.target.value)}
          style={{
            width: 80,
            padding: "4px 6px",
            background: "#08080d",
            border: "1px solid #2a2a36",
            borderRadius: 4,
            color: "#e8e8ef",
            fontSize: 11,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
          placeholder="SOL"
        />
        <button
          onClick={onRun}
          disabled={btnDisabled}
          style={{
            padding: "4px 10px",
            background: btnDisabled ? "#22222e" : "#3a3a4e",
            color: btnDisabled ? "#5a5a72" : "#e8e8ef",
            border: "1px solid #2a2a36",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            cursor: btnDisabled ? "not-allowed" : "pointer",
          }}
        >
          Latch
        </button>
      </div>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={{ color: "#e8e8ef", fontSize: 12, fontWeight: 600 }}>
          koth_latch_milestone
        </div>
        <div style={{ color: "#7a8098", fontSize: 11, marginTop: 2 }}>
          Keeper-only. Posts a market-cap value (encoded as SOL → lamports);
          when it exceeds both season.peak_mcap and the next-milestone
          threshold, vault_fraction_bps of the PayoutVault is split across the
          current top-K LeaderboardEntries (credit-only; winners pull via
          claim_round_payout).
        </div>
        {result.output && (
          <div
            style={{
              color: "#cfd2dc",
              fontSize: 11,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              marginTop: 4,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {result.output}
          </div>
        )}
        {result.txSig && (
          <div
            style={{
              color: "#7ab0ff",
              fontSize: 11,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              marginTop: 4,
            }}
          >
            tx: {result.txSig.slice(0, 8)}…{result.txSig.slice(-6)}
          </div>
        )}
      </div>
      <div style={{ flex: "0 0 auto" }}>
        <StatusPillInline status={result.status} />
      </div>
    </div>
  );
}

function QuickBtn({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "4px 8px",
        background: disabled ? "#22222e" : "#3a3a4e",
        color: disabled ? "#5a5a72" : "#e8e8ef",
        border: "1px solid #2a2a36",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        minWidth: 44,
      }}
    >
      {label}
    </button>
  );
}

function StatusPillInline({
  status,
}: {
  status: TestResult["status"];
}): JSX.Element {
  const tone =
    status === "running"
      ? { bg: "#23344f", fg: "#7ab0ff", label: "RUNNING" }
      : status === "ok"
        ? { bg: "#1c3a26", fg: "#7af09e", label: "OK" }
        : status === "fail"
          ? { bg: "#3a1c26", fg: "#f47a8e", label: "FAIL" }
          : { bg: "#22222e", fg: "#9aa0b4", label: "IDLE" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        background: tone.bg,
        color: tone.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.5,
        minWidth: 56,
        textAlign: "center",
      }}
    >
      {tone.label}
    </span>
  );
}

function SessionLoader({
  input,
  onInputChange,
  onLoad,
  onClear,
  hasTracked,
}: {
  input: string;
  onInputChange: (v: string) => void;
  onLoad: () => void;
  onClear: () => void;
  hasTracked: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        padding: "8px 10px",
        background: "#0e0e15",
        borderBottom: "1px solid #1c1c26",
        display: "flex",
        gap: 6,
        alignItems: "center",
      }}
    >
      <input
        type="text"
        placeholder="paste session_id (64 hex chars) to track an existing session"
        value={input}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onLoad();
        }}
        style={{
          flex: 1,
          padding: "5px 8px",
          background: "#08080d",
          border: "1px solid #2a2a36",
          borderRadius: 4,
          color: "#e8e8ef",
          fontSize: 11,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      />
      <button
        onClick={onLoad}
        style={{
          padding: "5px 10px",
          background: "#3a3a4e",
          color: "#e8e8ef",
          border: "1px solid #2a2a36",
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Load
      </button>
      {hasTracked && (
        <button
          onClick={onClear}
          style={{
            padding: "5px 8px",
            background: "transparent",
            color: "#9aa0b4",
            border: "1px solid #2a2a36",
            borderRadius: 4,
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

function SessionContextBar({
  sessionId,
  sessionPda,
  session,
}: {
  sessionId: string | null;
  sessionPda: string | null;
  session: SessionState | null;
}): JSX.Element {
  const statusLabel = session
    ? ["Created", "Live", "ResultSubmitted", "Paid"][session.status] ??
      `Unknown(${session.status})`
    : sessionId
      ? "PDA fetching… (or session_id has no on-chain account yet)"
      : "no session tracked — run create_session or paste a session_id below";
  return (
    <div
      style={{
        padding: "8px 10px",
        background: "#0a0a12",
        borderBottom: "1px solid #1c1c26",
        color: "#cfd2dc",
        fontSize: 11,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        lineHeight: 1.6,
      }}
    >
      <div>
        session_id:{" "}
        {sessionId
          ? `${sessionId.slice(0, 8)}…${sessionId.slice(-6)}`
          : "(none)"}
      </div>
      <div>
        pda:{" "}
        {sessionPda
          ? `${sessionPda.slice(0, 8)}…${sessionPda.slice(-6)}`
          : "(none)"}
      </div>
      <div>state: {statusLabel}</div>
      {session && (
        <div>
          host: {session.host?.toBase58().slice(0, 8) ?? "(undefined)"}… ·
          escrow: {lamportsToSol(BigInt(session.escrowLamports.toString()))} SOL ·
          players: {session.players.length} ·
          winner:{" "}
          {session.winner.equals(PublicKey.default)
            ? "(unset)"
            : `${session.winner.toBase58().slice(0, 8)}…`}
        </div>
      )}
    </div>
  );
}

function SessionFormBar({
  hostRegion,
  onHostRegionChange,
  hostCapacity,
  onHostCapacityChange,
  escrowSolInput,
  onEscrowSolChange,
  playersInput,
  onPlayersChange,
  stateHashInput,
  onStateHashChange,
  winnerInput,
  onWinnerChange,
  stateJsonInput,
  onStateJsonChange,
  onHashStateJson,
  onSelfWinner,
}: {
  hostRegion: string;
  onHostRegionChange: (v: string) => void;
  hostCapacity: string;
  onHostCapacityChange: (v: string) => void;
  escrowSolInput: string;
  onEscrowSolChange: (v: string) => void;
  playersInput: string;
  onPlayersChange: (v: string) => void;
  stateHashInput: string;
  onStateHashChange: (v: string) => void;
  winnerInput: string;
  onWinnerChange: (v: string) => void;
  stateJsonInput: string;
  onStateJsonChange: (v: string) => void;
  onHashStateJson: () => void;
  onSelfWinner: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        padding: "8px 10px",
        background: "#0e0e15",
        borderBottom: "1px solid #1c1c26",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <ConfigRow
        label="host region"
        input={
          <input
            type="text"
            value={hostRegion}
            onChange={(e) => onHostRegionChange(e.target.value)}
            maxLength={16}
            style={configInputStyle}
          />
        }
        hint="Free-form region tag (≤16 bytes). Used by register_host."
      />
      <ConfigRow
        label="host capacity"
        input={
          <input
            type="number"
            min={1}
            step={1}
            value={hostCapacity}
            onChange={(e) => onHostCapacityChange(e.target.value)}
            style={configInputStyle}
          />
        }
        hint="Self-reported player capacity for register_host."
      />
      <ConfigRow
        label="escrow (SOL)"
        input={
          <input
            type="number"
            min={0}
            step={0.001}
            value={escrowSolInput}
            onChange={(e) => onEscrowSolChange(e.target.value)}
            style={configInputStyle}
          />
        }
        hint="Lamports = floor(SOL × 1e9). Funded from the connected wallet on create_session."
      />
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div
          style={{
            color: "#7a8098",
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: 1,
            minWidth: 130,
            paddingTop: 4,
          }}
        >
          players (one per line)
        </div>
        <textarea
          value={playersInput}
          onChange={(e) => onPlayersChange(e.target.value)}
          placeholder="base58 pubkey&#10;base58 pubkey&#10;…"
          rows={3}
          style={{
            ...configInputStyle,
            width: 380,
            height: "auto",
            resize: "vertical",
          }}
        />
        <div
          style={{
            color: "#7a8098",
            fontSize: 11,
            flex: 1,
            paddingTop: 4,
          }}
        >
          Up to 8 wallets allowed in the session. Trimmed and parsed via
          new PublicKey() on submit.
        </div>
      </div>
      <ConfigRow
        label="winner pubkey"
        input={
          <span style={{ display: "flex", gap: 4 }}>
            <input
              type="text"
              value={winnerInput}
              onChange={(e) => onWinnerChange(e.target.value)}
              placeholder="base58"
              style={{ ...configInputStyle, width: 240 }}
            />
            <button
              onClick={onSelfWinner}
              style={{
                padding: "4px 8px",
                background: "#3a3a4e",
                color: "#e8e8ef",
                border: "1px solid #2a2a36",
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              self
            </button>
          </span>
        }
        hint="Winning player. submit_result writes this to session.winner."
      />
      <ConfigRow
        label="state_hash (hex)"
        input={
          <input
            type="text"
            value={stateHashInput}
            onChange={(e) => onStateHashChange(e.target.value)}
            placeholder="64 hex chars"
            style={{ ...configInputStyle, width: 380 }}
          />
        }
        hint="32-byte hash written by submit_result. Use the helper below to derive from a JSON snapshot."
      />
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div
          style={{
            color: "#7a8098",
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: 1,
            minWidth: 130,
            paddingTop: 4,
          }}
        >
          state JSON (for hash)
        </div>
        <textarea
          value={stateJsonInput}
          onChange={(e) => onStateJsonChange(e.target.value)}
          rows={2}
          style={{
            ...configInputStyle,
            width: 380,
            height: "auto",
            resize: "vertical",
          }}
        />
        <button
          onClick={onHashStateJson}
          style={{
            padding: "4px 10px",
            background: "#3a3a4e",
            color: "#e8e8ef",
            border: "1px solid #2a2a36",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            alignSelf: "flex-start",
          }}
        >
          hash → state_hash
        </button>
      </div>
    </div>
  );
}
